import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import API_BASE from "../config/api";
import { isDefaultStoreRow, getStoreId } from "../hooks/useMerchantStores";
import {
  clearStaleAdminMerchantModeFlag,
  enterPromoLoginMode,
} from "../utils/adminMerchantMode";

const PromoLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verified, setVerified] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const LoginDevice = async (customerId, phone, storeId) => {
    try {
      const response = await axios.post(`${API_BASE}/promoLogin`, null, {
        params: { customerId, phone },
      });

      // ✅ API SUCCESS → /modes
      if (response.status === 200 && response.data?.success === true) {
        const { data, token } = response.data;

        // 🔹 Established session
        localStorage.setItem("phone", data.mobile);
        if (token) localStorage.setItem("auth_token", token);
        if (data.id) localStorage.setItem("userId", data.id);
        // getCustomerByPromo (Backend/database/mysqlDB.js) already returns
        // the merchant's account_user.name — cache it so Home/Settings can
        // show "Welcome, <name>" without a Firebase uid to look it up with.
        // (/merchantDetailsByCustomer/:customer_id, the endpoint those
        // screens would otherwise fall back to, is disabled backend-side.)
        if (data.name) localStorage.setItem("merchant_name", data.name);

        // Resolve which store this device should land on by passing data.id
        // (account_user.id) to getMerchantStoreDetails as `userId` — the
        // promo link's own customerId isn't guaranteed to be the merchant's
        // default store for a multi-store account. Preference order:
        //   1. the store the link explicitly names (?storeId=…, e.g. a QR
        //      code printed for one specific location)
        //   2. the account's DEFAULT store
        //   3. falls back to promoLogin's own customer_id if this call
        //      fails or the account has no linked stores yet.
        let resolvedCustomerId = data.customer_id;
        if (data.id) {
          try {
            const storesRes = await axios.post(`${API_BASE}/getMerchantStoreDetails`, {
              userId: data.id,
            });
            const stores = Array.isArray(storesRes.data?.data) ? storesRes.data.data : [];
            const requestedStore = storeId
              ? stores.find((store) => String(getStoreId(store)) === String(storeId))
              : null;
            const targetStore = requestedStore || stores.find(isDefaultStoreRow) || stores[0];
            if (targetStore?.customer_id) {
              resolvedCustomerId = targetStore.customer_id;
            }
          } catch (storeErr) {
            console.error(
              "getMerchantStoreDetails failed, falling back to promoLogin's customer_id:",
              storeErr,
            );
          }
        }
        localStorage.setItem("customer_id", resolvedCustomerId);

        // Real merchant device login — don't inherit a stale Admin
        // Merchant Mode flag from a previous impersonation session, then
        // mark this as a Promo Login session so store-scoped screens
        // (Mode/Schedule/AI Menu/Settings) know to trust this customer_id
        // directly instead of resolving it through the Firebase-uid-based
        // store hook — this device never signs into Firebase.
        clearStaleAdminMerchantModeFlag();
        enterPromoLoginMode();

        setVerified(true);

        navigate("/modes", {
          replace: true,
          state: { verifiedMsg: "Device verified successfully!" },
        });
        return;
      }

      // ❌ API FAILURE → /login
      handleFailure(response.data?.message);
    } catch (error) {
      console.error("Device login error:", error);
      // A disabled team member (403, same gate merchantUserDetails applies
      // for the OTP login path — see Backend/controllers/merchantController.js
      // promoLogin) has a specific, useful message; surface it instead of
      // the generic fallback.
      handleFailure(error.response?.data?.message || "Something went wrong. Please try again.");
    }
  };

  const handleFailure = (message) => {
    navigate("/login", {
      replace: true,
      state: { errorMsg: message || "Customer not found" },
    });
  };

  const isMounted = React.useRef(false);

  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;

    // Accept both the camelCase params LoginDevice/promoLogin use
    // (customerId, phone) AND the snake_case/plain-English names people
    // reach for when hand-building a link (customer_id, mobile) — a link
    // built with the "wrong" name here previously read as `null`, tripped
    // the early redirect below with no error message at all, and looked
    // identical to "this feature is broken" instead of "wrong param name".
    const customerId = (
      searchParams.get("customerId") || searchParams.get("customer_id") || ""
    ).trim();
    const rawPhone =
      searchParams.get("phone") ||
      searchParams.get("mobile") ||
      searchParams.get("mobile_number") ||
      "";
    // URLSearchParams decodes a literal `+` in a query value as a space
    // (the application/x-www-form-urlencoded convention) — a link built as
    // `?phone=+15551234567` instead of the properly percent-encoded
    // `?phone=%2B15551234567` arrives here as " 15551234567". That never
    // matches account_user.mobile (which is stored as "+15551234567"),
    // getCustomerByPromo returns no row, and LoginDevice below reports
    // "Invalid credentials" and bounces to /login — indistinguishable from
    // a genuinely wrong number unless you go looking. Phone numbers never
    // legitimately contain a space, so reversing it here is safe.
    const phone = rawPhone.replace(/ /g, "+").trim();
    // Optional — pins this device to one specific store (e.g. a QR code
    // printed for a single location) instead of the account's default
    // store. Same identifier used elsewhere (promoController.js,
    // modeController.js): the store's own numeric id.
    const storeId = searchParams.get("storeId") || searchParams.get("store_id") || "";

    if (!customerId || !phone) {
      // No error message shown before — indistinguishable from a real
      // failure. Now at least visible on /login (see handleFailure below).
      navigate("/login", {
        replace: true,
        state: {
          errorMsg: "This promo login link is missing the customer_id or mobile number.",
        },
      });
      return;
    }

    LoginDevice(customerId, phone, storeId);
  }, [navigate, searchParams]);

  return (
    <div style={{ padding: "40px", textAlign: "center" }}>
      {verified ? (
        <h2>Verified ✅</h2>
      ) : errorMsg ? (
        <h2 style={{ color: "red" }}>{errorMsg}</h2>
      ) : (
        <h2>Verifying Device...</h2>
      )}
    </div>
  );
};

export default PromoLogin;
